import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../data/content";
import { LineReveal, Rise } from "./motion/Reveal";

const CaseStudy = ({ project, onClose }) => (
  <motion.div
    data-testid="case-study-modal"
    data-lenis-prevent
    className="fixed inset-0 z-[1000] overflow-y-auto bg-ink/95 backdrop-blur-xl"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="mx-auto max-w-[1100px] px-5 py-10 md:px-10 md:py-16">
      <button
        data-testid="case-study-close"
        onClick={onClose}
        className="glass fixed right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full text-cream transition-colors hover:border-magenta/50 hover:text-magenta"
      >
        <X size={20} />
      </button>

      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-electric">{project.category} · {project.year}</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-none tracking-tighter text-cream md:text-7xl">
          {project.title}
        </h2>

        {project.image && (
          <div className="mt-8 overflow-hidden rounded-2xl bg-surface">
            <img src={project.image} alt={project.title} className="mx-auto max-h-[75vh] w-full object-contain" />
          </div>
        )}

        <p className="mt-10 max-w-3xl font-body text-xl font-light leading-relaxed text-white/80 md:text-2xl">{project.blurb}</p>

        <div className="mt-12 grid gap-8 md:grid-cols-3">
          {[["Design Challenge", project.challenge], ["Research", project.research], ["Final Outcome", project.outcome]].map(([k, v]) => (
            <div key={k} className="border-t border-white/10 pt-5">
              <h4 className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-magenta">{k}</h4>
              <p className="mt-3 font-body text-sm font-light leading-relaxed text-white/60">{v}</p>
            </div>
          ))}
        </div>

        {project.gallery?.length > 0 && (
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {project.gallery.map((g, i) => (
              <div key={i} className="overflow-hidden rounded-xl bg-surface">
                {g.endsWith(".mp4") ? (
                  <video data-testid={`gallery-video-${i}`} src={g} controls muted loop playsInline preload="metadata" className="max-h-[70vh] w-full object-contain" />
                ) : (
                  <img src={g} alt={`${project.title} ${i + 1}`} loading="lazy" className="max-h-[70vh] w-full object-contain" />
                )}
              </div>
            ))}
          </div>
        )}

        {project.presentation && (
          <div className="mt-12">
            <h4 className="mb-4 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-electric">Presentation Deck</h4>
            <div className="relative overflow-hidden rounded-2xl border border-white/10" style={{ paddingTop: "56.25%" }}>
              <iframe
                title={`${project.title} presentation`}
                src={project.presentation}
                loading="lazy"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
                style={{ border: "none" }}
              />
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {project.tools.map((t) => (
            <span key={t} className="glass rounded-full px-4 py-2 font-mono text-xs uppercase tracking-wider text-white/70">{t}</span>
          ))}
        </div>
      </motion.div>
    </div>
  </motion.div>
);

const FILTERS = [
  { id: "design", label: "Design Focused" },
  { id: "technical", label: "Technical / Pattern Making" },
  { id: "clo", label: "CLO Design" },
];

export const Work = () => {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("design");

  const filtered = PROJECTS.filter((p) => p.track === filter);

  return (
    <section id="work" data-testid="work-section" className="relative mx-auto max-w-[1400px] px-5 py-28 md:px-12 md:py-40">
      <div className="mb-8 flex items-end justify-between gap-4">
        <h2 className="font-display text-5xl font-extrabold uppercase tracking-tighter md:text-8xl">
          <LineReveal lines={["Featured", "Work"]} />
        </h2>
        <span className="hidden font-mono text-xs uppercase tracking-[0.3em] text-white/40 md:block">({String(filtered.length).padStart(2, "0")} Selected)</span>
      </div>

      <div className="mb-12 flex flex-wrap gap-2" data-testid="work-filters">
        {FILTERS.map((f) => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`work-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className="relative rounded-full px-5 py-2.5 text-sm font-medium transition-colors"
              style={{ color: isActive ? "rgb(var(--ink))" : "rgb(var(--cream) / 0.6)" }}
            >
              {isActive && (
                <motion.span layoutId="work-filter-pill" className="absolute inset-0 rounded-full bg-cream" transition={{ type: "spring", stiffness: 400, damping: 34 }} />
              )}
              <span className="relative z-10">{f.label}</span>
            </button>
          );
        })}
      </div>

      <motion.div layout className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
        {filtered.map((p, i) => (
          <Rise key={p.id} delay={i * 0.06} className={p.span}>
            <motion.button
              data-testid={`work-item-${p.id}`}
              onClick={() => setSelected(p)}
              whileHover="hover"
              className="group relative block h-full min-h-[300px] w-full overflow-hidden rounded-2xl border border-white/10 text-left"
            >
              {(p.thumb || p.image) ? <motion.img
                src={p.thumb || p.image}
                alt={p.title}
                loading="lazy"
                variants={{ hover: { scale: 1.08 } }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className={`absolute inset-0 h-full w-full object-cover brightness-[0.72] ${p.thumbPos || ""}`}
              /> : <div data-testid={`work-placeholder-${p.id}`} className="absolute inset-0 bg-gradient-to-br from-electric/25 via-surface to-magenta/30" />}
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <motion.div
                variants={{ hover: { opacity: 1 } }}
                initial={{ opacity: 0 }}
                className="absolute inset-0 bg-gradient-to-t from-magenta/40 via-transparent to-electric/20"
              />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-transparent transition-all duration-500 group-hover:ring-electric/60 group-hover:shadow-[0_0_50px_-5px_rgba(0,240,255,0.35)]" />

              <div className="relative flex h-full flex-col justify-between p-6">
                <div className="flex items-center justify-between">
                  <span className="glass rounded-full px-3 py-1.5 font-mono text-[0.62rem] uppercase tracking-wider text-cream">{p.category}</span>
                  <motion.span variants={{ hover: { rotate: 45, scale: 1.1 } }} className="text-cream">
                    <ArrowUpRight size={22} />
                  </motion.span>
                </div>
                <div>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-electric">{p.category} · {p.year}</p>
                  <h3 className="mt-2 font-heading text-2xl font-bold leading-tight text-cream md:text-3xl">{p.title}</h3>
                  <motion.p
                    variants={{ hover: { opacity: 1, height: "auto" } }}
                    initial={{ opacity: 0, height: 0 }}
                    className="mt-2 overflow-hidden font-body text-sm font-light text-white/70"
                  >
                    {p.blurb}
                  </motion.p>
                </div>
              </div>
            </motion.button>
          </Rise>
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div data-testid="work-empty" className="glass flex flex-col items-center justify-center gap-3 rounded-2xl px-6 py-24 text-center">
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-electric">Coming Soon</span>
          <p className="max-w-md font-heading text-2xl font-semibold text-cream md:text-3xl">
            CLO 3D projects are being added to this collection.
          </p>
          <p className="font-body text-sm font-light text-white/50">Check back shortly. Digital garment renders and virtual fit work land here.</p>
        </div>
      )}

      <AnimatePresence>
        {selected && <CaseStudy project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};
